import React, { useState, useEffect } from "react";
import dayjs from "dayjs";

import { getReporting } from "../api/reporting";
import { getAllTokens } from "../api/token";

import { Box, TextField, Select, MenuItem, ListItemText, Chip, InputLabel, FormControl, Button, Checkbox, FormControlLabel } from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

import MultiLineChart from "../components/MultiLineChart";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { getAllNetworks } from "../api/network";

const Reporting = () => {
  // read from and to from url query params, they are timestamps
  const urlParams = new URLSearchParams(window.location.search);
  const from = urlParams.get('from');
  const to = urlParams.get('to');

  // convert to dayjs object from timestamp
  const from_date = from ? dayjs.unix(from) : dayjs().subtract(1, 'month');
  const to_date = to ? dayjs.unix(to) : dayjs();


  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [tokensList, setTokensList] = useState([]);
  const [networksList, setNetworksList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [config, setConfig] = useState({
    from: from_date,
    to: to_date,
    token_symbols: [],
    exclude_tokens: false,
    networks: [],
    granularity: "day",
    dimension: 'liquidity_rate',
  });

  const navigate = useNavigate();

  const fetchData = () => {

    const filterTokenList = (
        config.token_symbols.length > 0 && config.exclude_tokens
        ?
        tokensList.filter((token) => !config.token_symbols.includes(token.symbol))
        .map((token) => token.symbol)
        :
        config.token_symbols
    );

    getReporting({
      ...config,
      token_symbols: filterTokenList,
      group_by: ["token_symbol"],
    }).then((data) => {
      setSnapshotHistory(data);
      setIsLoading(false);
    });
  }

  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [config])

  useEffect(() => {
    getAllTokens().then((data) => {
      setTokensList(data);
    });

    getAllNetworks().then((data) => {
      setNetworksList(data);
    });
  }, []);

  const groupedHistory = {};

  snapshotHistory.forEach((snapshot) => {
    const key = snapshot.token_symbol;
    if (!groupedHistory[key]) {
      groupedHistory[key] = {
        key,
        data: [],
      };
    }
    groupedHistory[key].data.push({
        x: snapshot.timestamp * 1000,
        y: snapshot.average / (10 ** 25),
        prefix: snapshot.token_symbol,
        suffix: '%',
    });
  });

  const handleChange = (type, val) => {
    setConfig((prev) => ({
      ...prev,
      [type]: val,
    }));
  };


  const handleDateChange = (type, date) => {
    const timestamp = date.unix();
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(type, timestamp);
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
    handleChange(type, date);
  }

  return (
    <Layout title="Reporting" icon={<QueryStatsIcon />}>
    <Box sx={{
      width: '100%', 
      display: 'flex', 
      flexDirection: 'row', 
      justifyContent: 'center', 
      alignItems: 'center', 
      mb: 2, 
      gap: 2, 
      backgroundColor: 'var(--background-color)',
      pt: 2,
      pb: 1,
    }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="From Date"
          value={config.from}
          onChange={(newValue) => handleDateChange('from', newValue)}
          renderInput={(params) => 
            <TextField {...params} fullWidth margin="normal" />
          }
        />
        <DatePicker
          label="To Date"
          value={config.to}
          onChange={(newValue) => handleDateChange('to', newValue)}
          renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
        />
      </LocalizationProvider>
    </Box>
    <Box
    >
      <MultiLineChart
      lines={Object.entries(groupedHistory).map(([_, data]) => data)}
      isLoading={isLoading}
      onLegendClick={(tokenSymbol) => navigate(`/token/${tokenSymbol}?from=${config.from.unix()}&to=${config.to.unix()}`)}
      filter={<Box sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          borderRadius: 4,
          marginTop: 2,
      }}>

          <FormControl fullWidth margin="normal">
          <InputLabel id="label-granularity">Granularity</InputLabel>
          <Select
              labelId="label-granularity"
              value={config.granularity}
              onChange={(e) => handleChange('granularity', e.target.value)}
              label="Granularity"
          >
              <MenuItem key="day" value="day">Day</MenuItem>
              <MenuItem key="week" value="week">Week</MenuItem>
              <MenuItem key="month" value="month">Month</MenuItem>
          </Select>
          </FormControl>

          <Box sx={{ mt: 1 }}>
          <FormControl fullWidth margin="normal">
          <InputLabel id="dimension-select-label">Dimension</InputLabel>
          <Select
              labelId="dimension-select-label"
              value={config.dimension}
              onChange={(e) => handleChange('dimension', e.target.value)}
              label="Dimension"
          >
              <MenuItem key="liquidity_rate" value="liquidity_rate">Liquidity Rate</MenuItem>
              <MenuItem key="variable_borrow_rate" value="variable_borrow_rate">Variable Borrow Rate</MenuItem>
              <MenuItem key="stable_borrow_rate" value="stable_borrow_rate">Stable Borrow Rate</MenuItem>
          </Select>
          </FormControl>
          </Box>  

          <FormControlLabel
          sx={{ mt: 1 }}
          control={
              <Checkbox 
              checked={config.exclude_tokens}  
              onChange={(e) => handleChange('exclude_tokens', e.target.checked)}
              />
          }
          label="Exclude Tokens"
          />

          <FormControl fullWidth margin="normal">
          <InputLabel id="token-select-label">Tokens</InputLabel>
          <Select
          labelId="token-select-label"
          value={config.token_symbols}
          multiple
          onChange={(e) => {
              handleChange('token_symbols', e.target.value)
          }}
          renderValue={(selected) => (
              <Box sx={{ gap: 0.5, flexWrap: 'wrap', display: 'flex', overflowY: 'hidden' }}>
                  {selected.map((value) => (
                      <Chip key={value} label={value} />
                  ))}
              </Box>
          )}
          label="Tokens"
          >
              {
              tokensList.map((token) => (
                  <MenuItem key={`token-select-option-${token.id}`} value={token.symbol}>
                      <ListItemText primary={token.symbol} />
                  </MenuItem>
              ))
              }
          </Select>
          </FormControl>

          { config.token_symbols.length > 0 ?
          <Button 
          variant="outlined" 
          color="error" 
          sx={{ mt: 2 }} 
          onClick={() => handleChange('token_symbols', [])}
          >
          Clear All Tokens
          </Button> : null
          }

            <FormControl fullWidth margin="normal">
            <InputLabel labelId="network-select-label">Networks</InputLabel>
            <Select
            label="Networks"
            labelId='network-select-label'
            value={config.networks}
            multiple
            onChange={(e) => handleChange('networks', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ gap: 0.5, flexWrap: 'wrap', display: 'flex', overflowY: 'hidden' }}>
                  {selected.map((value) => (
                      <Chip key={value} label={value} />
                  ))}
              </Box>
          )}
            >
              {
                networksList.map((network) => (
                  <MenuItem key={network} value={network}>{network}</MenuItem>
                ))
              }
            </Select>
          </FormControl>

          { config.networks.length > 0 ?
          <Button 
          variant="outlined" 
          color="error" 
          sx={{ mt: 2 }} 
          onClick={() => handleChange('networks', [])}
          >
          Clear All Networks
          </Button> : null
          }
      </Box>}
      />
    </Box>
    </Layout>
  );


};

export default Reporting;